import { useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useToast } from '@chakra-ui/react';
import { WS_EVENTS } from '@twinden/shared';
import { currentFamilyIdAtom, currentUserAtom } from '../store';
import { t } from '../i18n';
import { useFamily } from './useFamily';
import { useWebSocket } from './useWebSocket';

type DecidedPayload = {
  familyId?: string;
  userId?: string;
  status?: 'approved' | 'declined' | 'cancelled';
};

// A family let me in (D155): load it, open it and say so. Mounted once, in
// the app shell, so it works with or without other families.
export function useMyJoinDecisions() {
  const { onEvent } = useWebSocket();
  const { loadFamilies } = useFamily();
  const me = useAtomValue(currentUserAtom)?.id;
  const setCurrentFamilyId = useSetAtom(currentFamilyIdAtom);
  const toast = useToast();
  // useToast returns a new function each render; keep the effect stable.
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        if (event !== WS_EVENTS.JOIN_REQUEST_DECIDED) return;
        const { familyId, userId, status } = payload as DecidedPayload;
        if (!familyId || userId !== me || status !== 'approved') return;
        void loadFamilies().then((families) => {
          const family = families.find((f) => f.id === familyId);
          if (!family) return;
          setCurrentFamilyId(family.id);
          toastRef.current({
            status: 'success',
            title: t('account.waiting.youreIn', { family: family.name }),
          });
        });
      }),
    [onEvent, me, loadFamilies, setCurrentFamilyId]
  );
}
